import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode, type HTMLAttributes, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../cn';
import { MODAL_LAYER_ATTRIBUTE, closeModal, holdPageBehind, invoker, openModal, openModals, popoverOriginOf } from './modalLayers';
import styles from './Modal.module.css';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Set to false to disable Esc / backdrop-click dismiss. Used for destructive confirms. */
  dismissible?: boolean;
  width?: number;
  children: ReactNode;
  labelledBy?: string;
  /**
   * Where focus goes back to on close. Without it, the element that had focus
   * when the modal opened, read before anything in it can take focus.
   */
  returnFocusRef?: RefObject<HTMLElement | null>;
}

// Context for ModalHeader to register its id so the <div role="dialog"> can
// set aria-labelledby without the caller threading an id explicitly.
import { createContext, useContext } from 'react';

const ModalContext = createContext<{ registerHeader(id: string): void } | null>(null);

/** What a dialog focuses first when nothing in it has asked for focus: its first control. */
const FOCUSABLE = 'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

export function Modal({ open, onClose, dismissible = true, width = 440, children, labelledBy, returnFocusRef }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const [headerId, setHeaderId] = useState<string | undefined>(undefined);
  /**
   * The element focus was on as this modal opened (TOADO-984), and what
   * opened the popover it was in, if any: a menu item that opens a dialog is
   * gone by the time the dialog closes (review round 1).
   *
   * Read while rendering the open, because a field inside with `autoFocus`
   * takes focus as React commits it, before any effect runs: read later, the
   * opener is that field. Read on every render until the open has committed
   * (`openCommitted`, set in a layout effect, which runs before any later
   * render), so a render React throws away and does again reads afresh
   * rather than leaving a stale answer (review round 1).
   */
  const opener = useRef<HTMLElement | null>(null);
  const origin = useRef<HTMLElement | null>(null);
  const openCommitted = useRef(false);
  if (open && !openCommitted.current) {
    opener.current = invoker();
    origin.current = popoverOriginOf(opener.current);
  }
  useLayoutEffect(() => { openCommitted.current = open; }, [open]);
  const returnFocusRefRef = useRef(returnFocusRef);
  returnFocusRefRef.current = returnFocusRef;

  // Refs for the callback / flag so the open-transition effect's deps
  // can be just [open]. Without this, callers passing inline arrow
  // functions (the common pattern) would cause the effect to re-run
  // on EVERY parent render — and the 10 ms focus-grab below would
  // steal focus to the first focusable in the dialog on each tick,
  // making it impossible to type in any input inside the modal once
  // the parent re-renders for a state-bound input. Hit by the
  // CommentsSection expand-modal on 2026-05-02.
  const onCloseRef = useRef(onClose);
  const dismissibleRef = useRef(dismissible);
  onCloseRef.current = onClose;
  dismissibleRef.current = dismissible;

  useEffect(() => {
    if (!open) return;
    // Held here, not read in the cleanup: React has let go of its refs by then.
    const dialog = dialogRef.current;
    const self = openModal({
      opener: returnFocusRefRef.current?.current ?? opener.current,
      origin: origin.current,
      dialog,
      layer: backdropRef.current,
    });
    // Only the newest answers Escape, so a modal opened over another (DevTools
    // over the Session window) closes back to it instead of taking both down.
    const onKey = (e: KeyboardEvent) => {
      if (openModals[openModals.length - 1] !== self) return;
      // Anything open in the dialog that claims Escape, a popover or an inline
      // editor, closes alone (TOADO-984). On the window rather than the document
      // for this: a popover listens on the document, so it hears the key first
      // however late it opened, before React re-renders it out of the dispatch.
      // A page's own shortcuts keep quiet while a modal is open (isModalOpen),
      // so none of them claims it first. A key pressed inside an iframe in the
      // dialog never reaches this window: Escape there closes nothing.
      if (e.defaultPrevented) return;
      if (dismissibleRef.current && e.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    // The page behind out of reach while this is open (TOADO-984; see modalLayers).
    const releasePage = holdPageBehind(self);
    const t = setTimeout(() => {
      // A field that took focus as it mounted (`autoFocus`) keeps it.
      if (dialog?.contains(document.activeElement)) return;
      // `data-autofocus` names the first focus for a caller.
      const auto = dialog?.querySelector<HTMLElement>('[autofocus], [data-autofocus]');
      if (auto) {
        auto.focus();
        return;
      }
      // The dialog itself where it holds nothing to focus, so focus is never left behind it.
      (dialog?.querySelector<HTMLElement>(FOCUSABLE) ?? dialog)?.focus();
    }, 10);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      clearTimeout(t);
      // The page let go of first: focus cannot go back into an inert one.
      releasePage();
      /**
       * Focus given back a microtask later, not here (review round 1). A
       * StrictMode replay runs this cleanup and the setup again at once:
       * given back here, focus left a field that took it with `autoFocus`,
       * which the replay does not focus again. Later, the replay's setup has
       * put the modal back on the stack as a newer one, and the giving back
       * steps aside for it, as it does for any modal opened meanwhile.
       */
      const giveBack = closeModal(self);
      if (giveBack) void Promise.resolve().then(giveBack);
    };
  }, [open]);

  if (!open) return null;

  // Portal to document.body so the backdrop + dialog escape any
  // stacking context an ancestor created (transform, filter,
  // contain, will-change, isolation, or a positioned ancestor with
  // a lower z-index than the cards). Without this, a Modal opened
  // from inside the Sidebar can render under the kanban cards
  // because the Sidebar's stacking context caps z-index inside it.
  return createPortal(
    <ModalContext.Provider value={{ registerHeader: setHeaderId }}>
      <div
        ref={backdropRef}
        {...{ [MODAL_LAYER_ATTRIBUTE]: '' }}
        className={styles.backdrop}
        onClick={(e) => {
          if (dismissible && e.target === e.currentTarget) onClose();
        }}
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledBy ?? headerId}
          tabIndex={-1}
          className={styles.dialog}
          style={{ width }}
        >
          {children}
        </div>
      </div>
    </ModalContext.Provider>,
    document.body,
  );
}

export function ModalHeader({ className, children, id, ...rest }: HTMLAttributes<HTMLDivElement>) {
  const auto = useId();
  const effectiveId = id ?? auto;
  const ctx = useContext(ModalContext);
  useEffect(() => { ctx?.registerHeader(effectiveId); }, [ctx, effectiveId]);
  return <div id={effectiveId} className={cn(styles.header, className)} {...rest}>{children}</div>;
}
export function ModalBody({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn(styles.body, className)} {...rest}>{children}</div>;
}
export function ModalFooter({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn(styles.footer, className)} {...rest}>{children}</div>;
}
